import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

const GoalsContext = createContext();

export const useGoals = () => {
	const ctx = useContext(GoalsContext);
	if (!ctx) throw new Error('useGoals must be used within GoalsProvider');
	return ctx;
};

export const GoalsProvider = ({ children }) => {
	const { token, isAuthenticated } = useAuth();
	const [goals, setGoals] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const fetchGoals = async () => {
			if (!isAuthenticated || !token) {
				setGoals([]);
				return;
			}
			setLoading(true);
			try {
				const data = await api.getGoals(token);
				setGoals(data || []);
			} catch (e) {
				console.warn('Failed to fetch goals', e);
			} finally {
				setLoading(false);
			}
		};
		fetchGoals();
	}, [token, isAuthenticated]);

	const addGoal = async (goal) => {
		try {
			const newGoal = await api.createGoal(goal, token);
			setGoals(g => [...g, newGoal]);
			return newGoal;
		} catch (e) {
			console.error('Failed to add goal:', e);
			throw e;
		}
	};

	const updateGoal = async (id, patch) => {
		try {
			const updated = await api.updateGoal(id, patch, token);
			setGoals(g => g.map(goal => goal.id === id ? updated : goal));
			return updated;
		} catch (e) {
			console.error('Failed to update goal:', e);
			throw e;
		}
	};

	const deleteGoal = async (id) => {
		try {
			await api.deleteGoal(id, token);
			setGoals(g => g.filter(goal => goal.id !== id));
		} catch (e) {
			console.error('Failed to delete goal:', e);
			throw e;
		}
	};

	const incrementProgress = async (id, amount) => {
		const goal = goals.find(g => g.id === id);
		if (!goal) return;
		const newAmount = Math.min(goal.target, (goal.currentAmount || 0) + amount);
		const progress = Math.round((newAmount / goal.target) * 100);
		try {
			await updateGoal(id, { currentAmount: newAmount, progress });
		} catch (e) {
			console.error('Failed to update progress:', e);
		}
	};

	const activeGoals = goals.filter(g => (g.progress || 0) < 100);
	const completedGoals = goals.filter(g => (g.progress || 0) >= 100);

	return (
		<GoalsContext.Provider value={{ goals, activeGoals, completedGoals, loading, addGoal, updateGoal, deleteGoal, incrementProgress }}>
			{children}
		</GoalsContext.Provider>
	);
};

export default GoalsContext;
